import { PageController } from '@client/components/_core/layout/PageController';
import { ClaimSendModal } from '@client/components/_shared/comms/ClaimSendModal';
import { CommsInbox } from '@client/components/_shared/comms/CommsInbox';
import { threadSummaryFromLatest } from '@client/components/_shared/comms/comm-item';
import { DownloadOriginalButton } from '@client/components/_shared/comms/DownloadOriginalButton';
import { buildDraftComposeState, draftThreadSummary, withoutDraft } from '@client/components/_shared/comms/drafts';
import { EntityUploadEmailControl } from '@client/components/_shared/comms/EntityUploadEmailControl';
import type { CommsInboxAdapter } from '@client/components/_shared/comms/types';
import { useCommsInboxState } from '@client/components/_shared/comms/useCommsInboxState';
import { useMarkThreadRead } from '@client/components/_shared/comms/useMarkThreadRead';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { EmailDeliveryStatus } from '@client/components/claims/comm-log-message/EmailDeliveryStatus';
import { CommsFilters } from '@client/components/comms-filters/CommsFilters';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { claimFormStore, useHasClaimWriteAccess } from '@client/routes/~console.$orgId/~claims/~details.$id/store';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo, useRef } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/comms')({
    // `messageId` deep-link is used by global notifications to open the inbox on a message.
    validateSearch: zodValidator(z.object({ messageId: z.string().optional() })),
    component: ClaimCommsPage,
});

function ClaimCommsPage() {
    return (
        <PageController route={Route}>
            <ClaimComms />
        </PageController>
    );
}

function ClaimComms() {
    const { id } = Route.useParams();
    const { messageId } = Route.useSearch();
    const { trpc, trpcUtils } = useTrpc();
    // CUR-725: closed claims are read-only — no compose/reply/forward (inbound mail still arrives).
    // CUR-2675: so is a claim the viewer may read but not edit (the Accounts role), with no upload either.
    const isReadonly = claimFormStore.use.isReadonly();
    const canWrite = useHasClaimWriteAccess();

    const initialAnchorId = useMemo(() => {
        if (!messageId) return null;
        try {
            return BigInt(messageId);
        } catch {
            return null;
        }
    }, [messageId]);

    const inbox = useCommsInboxState({ initialAnchorId });

    // The list pages server-side; `placeholderData` keeps the current page on screen while the next
    // one (or a filter change) loads, so the inbox never blanks between pages.
    const listQuery = trpc.communication.listClaimCommThreads.useQuery(
        {
            claimId: id,
            where: Object.keys(inbox.where).length ? inbox.where : undefined,
            offset: inbox.offset,
            limit: inbox.limit,
        },
        { placeholderData: (prev) => prev, meta: { errorSurface: 'inline' } },
    );

    // The reading pane lazily loads the selected conversation; no `placeholderData` so switching threads
    // shows the loading state rather than the previous thread's messages.
    const threadQuery = trpc.communication.getClaimCommThread.useQuery(
        inbox.selectedThreadId !== null ? { claimId: id, anchorId: inbox.selectedThreadId } : skipToken,
        { meta: { errorSurface: 'inline' } },
    );

    // Opening a thread marks the whole conversation read (mirrors the WM inbox) and clears this
    // claim's unread badges in the cache without waiting for a refetch. The row patch can miss a
    // deep-linked thread (`selectedThreadId` may be a non-root message id), so the list is also
    // invalidated to reconcile against the server.
    const selectedThreadId = inbox.selectedThreadId;
    const handleThreadMarkedRead = useCallback(
        (markedIds: ReadonlySet<bigint>) => {
            if (selectedThreadId !== null) {
                trpcUtils.communication.getClaimCommThread.setData({ claimId: id, anchorId: selectedThreadId }, (old) =>
                    old?.map((message) => (markedIds.has(message.id) ? { ...message, unread: false } : message)),
                );
            }
            trpcUtils.communication.listClaimCommThreads.setQueriesData({ claimId: id }, { exact: false }, (old) =>
                old
                    ? {
                          ...old,
                          rows: old.rows.map((row) =>
                              markedIds.has(row.threadId) ||
                              markedIds.has(row.latest.id) ||
                              row.threadId === selectedThreadId
                                  ? { ...row, latest: { ...row.latest, unread: false }, unreadCount: 0 }
                                  : row,
                          ),
                      }
                    : old,
            );
            void trpcUtils.communication.listClaimCommThreads.invalidate({ claimId: id });
        },
        [id, selectedThreadId, trpcUtils],
    );
    useMarkThreadRead({ messages: threadQuery.data, onMarked: handleThreadMarkedRead });

    // From/To filter options span the whole claim (not just the loaded page); cached briefly.
    const facetsQuery = trpc.communication.getClaimCommFacets.useQuery({ claimId: id }, { staleTime: 5 * 60 * 1000 });

    // The caller's unsent email drafts (CUR-361) — a separate query because the thread list
    // deliberately excludes DRAFT rows; shown by the inbox's Drafts segment.
    const draftsQuery = trpc.communication.listClaimEmailDrafts.useQuery({ claimId: id });
    const drafts = useMemo(() => {
        const data = draftsQuery.data;
        // Deliberately `undefined`, not `[]`: pending and failed queries must stay distinguishable
        // from "this record has no drafts", or Reply would open blank over a saved draft (CUR-724).
        if (!data) return undefined;
        return data.drafts.map((draft) => ({
            summary: draftThreadSummary(draft),
            compose: buildDraftComposeState(draft, data.permissions),
            // Lets Reply on a thread reopen that thread's saved draft (CUR-724).
            rootId: draft.rootId,
        }));
    }, [draftsQuery.data]);

    // CUR-827: a closed claim can no longer resume or send a draft, so the inbox's Drafts segment
    // offers Discard as its only action — without it these drafts would be unreachable for good.
    // The composer owns deletion while the claim is open, so this runs only in the read-only case.
    const discardDraftMutation = trpc.communication.deleteClaimEmailDraft.useMutation({
        onSuccess(_data, variables) {
            trpcUtils.communication.listClaimEmailDrafts.setData({ claimId: id }, (old) =>
                withoutDraft(old, variables.draftId),
            );
            void trpcUtils.communication.listClaimEmailDrafts.invalidate({ claimId: id });
            void trpcUtils.email.listEmails.invalidate();
            void trpcUtils.email.getEmailThread.invalidate();
        },
        onError(error) {
            errorMessage.show(error);
        },
    });
    const handleDiscardDraft = useCallback(
        // `mutateAsync` (deviating from the prefer-`mutate` guideline) so the confirm popover only
        // reports success once the server has actually deleted the draft.
        async (draftId: bigint) => {
            await discardDraftMutation.mutateAsync({ claimId: id, draftId });
        },
        // mutateAsync's identity is stable; the handler only depends on the claim.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [id],
    );

    // Memoised so the injected closures stay stable across the inbox's re-renders
    // (recreated only when the claim id changes).
    const adapter = useMemo<CommsInboxAdapter>(
        () => ({
            entity: 'claim',
            entityId: id,
            SendModal: (props) => <ClaimSendModal {...props} claimId={id} />,
            DeliveryStatus: EmailDeliveryStatus,
            // An inbound upload is the one addition a closed claim takes (CUR-725), from someone who may
            // edit it: the server refuses it without claim write access (CUR-2675).
            UploadEmailControl: canWrite
                ? (props) => <EntityUploadEmailControl {...props} entity="claim" entityId={id} />
                : undefined,
            DownloadOriginalButton: DownloadOriginalButton,
        }),
        [id, canWrite],
    );

    const threads = useMemo(
        () =>
            (listQuery.data?.rows ?? []).map((row) =>
                threadSummaryFromLatest(row.latest, {
                    threadId: row.threadId,
                    messageCount: row.messageCount,
                    unreadCount: row.unreadCount,
                }),
            ),
        [listQuery.data],
    );

    // CommsFilters owns the advanced filters (URL-persisted); capture its clear action so the inbox
    // empty-state "Clear filters" CTA can reset them alongside search/channel.
    const clearAdvancedFilters = useRef<() => void>(() => {});
    const registerClear = useCallback((clear: () => void) => {
        clearAdvancedFilters.current = clear;
    }, []);

    return (
        <>
            {/* Overlay child routes (view thread, reply, compose) layer on top — also the
                target of the Activity tab's email cross-links. */}
            <Outlet />

            {listQuery.isLoading ? (
                <Loading />
            ) : listQuery.error ? (
                <InlineQueryError error={listQuery.error} onRetry={() => void listQuery.refetch()} />
            ) : (
                <CommsInbox
                    state={inbox}
                    threads={threads}
                    total={listQuery.data?.total ?? 0}
                    listLoading={listQuery.isFetching}
                    threadMessages={threadQuery.data ?? []}
                    threadLoading={threadQuery.isFetching}
                    adapter={adapter}
                    drafts={drafts}
                    draftsLoading={draftsQuery.isLoading}
                    draftsError={draftsQuery.error}
                    onRetryDrafts={() => void draftsQuery.refetch()}
                    // Gated on the server's per-response permission envelope: an author who can
                    // still read the claim but has lost write access must not see a Discard that
                    // is guaranteed to fail with NOT_FOUND (deleteDraft requires write access).
                    onDiscardDraft={draftsQuery.data?.permissions.canDelete ? handleDiscardDraft : undefined}
                    onRefetch={() => {
                        void listQuery.refetch();
                        // Only when a thread is open — otherwise threadQuery is on `skipToken` and the
                        // refetch is a dead no-op.
                        if (inbox.selectedThreadId !== null) void threadQuery.refetch();
                        // Participants can change after a send/upload — keep the From/To facets fresh.
                        void facetsQuery.refetch();
                        // A send may have finalised a draft — drop it from the pinned Drafts group.
                        void trpcUtils.communication.listClaimEmailDrafts.invalidate({ claimId: id });
                        void trpcUtils.communication.listCommLogAndNotes.invalidate({ claimId: id });
                    }}
                    composeDisabled={isReadonly}
                    activeFilterCount={Object.keys(inbox.advancedWhere).length}
                    onClearFilters={() => clearAdvancedFilters.current()}
                    filterControls={
                        <CommsFilters
                            senders={facetsQuery.data?.senders}
                            recipients={facetsQuery.data?.recipients}
                            persistKey="claim-comms-filters"
                            onChange={inbox.setAdvancedWhere}
                            onRegisterClear={registerClear}
                        />
                    }
                />
            )}
        </>
    );
}
